'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Copy, CheckCircle2, AlertCircle, ArrowRight, GraduationCap, Shield, School, BookOpen, Users } from 'lucide-react';
import { toast } from 'sonner';
import AppImage from '@/components/ui/AppImage';
import { useAuth } from '@/contexts/AuthContext';

type Role = 'system-admin' | 'school-admin' | 'teacher' | 'parent' | 'learner';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const roles: { id: Role; label: string; icon: React.ReactNode; description: string; route: string }[] = [
  {
    id: 'system-admin',
    label: 'System Admin',
    icon: <Shield size={16} />,
    description: 'Platform governance & school management',
    route: '/system-admin-dashboard',
  },
  {
    id: 'school-admin',
    label: 'School Admin',
    icon: <School size={16} />,
    description: 'School operations & reporting',
    route: '/school-admin-dashboard',
  },
  {
    id: 'teacher',
    label: 'Teacher',
    icon: <BookOpen size={16} />,
    description: 'Classroom management & assessments',
    route: '/teacher-classroom-portal',
  },
  {
    id: 'parent',
    label: 'Parent / Guardian',
    icon: <Users size={16} />,
    description: 'Track learner progress & communicate',
    route: '/parent-learner-portal',
  },
  {
    id: 'learner',
    label: 'Learner',
    icon: <GraduationCap size={16} />,
    description: 'Homework, grades & timetable',
    route: '/parent-learner-portal',
  },
];

const demoCredentials: Record<Role, { email: string; password: string }> = {
  'system-admin': { email: 'sysadmin@learnerlink.co.za', password: 'Admin@LLS2026' },
  'school-admin': { email: 'principal@stmarys.edu.za', password: 'StMarys@2026' },
  teacher: { email: 'n.dlamini@stmarys.edu.za', password: 'Teacher@2026' },
  parent: { email: 'thembi.nkosi@gmail.com', password: 'Parent@2026' },
  learner: { email: 'learner.sipho@stmarys.edu.za', password: 'Learner@2026' },
};

// Map DB role values to frontend route
const DB_ROLE_ROUTE: Record<string, string> = {
  system_admin: '/system-admin-dashboard',
  school_admin: '/school-admin-dashboard',
  teacher: '/teacher-classroom-portal',
  parent: '/parent-learner-portal',
  learner: '/parent-learner-portal',
};

export default function LoginPageClient() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [selectedRole, setSelectedRole] = useState<Role>('school-admin');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const handleRoleSelect = (role: Role) => {
    setSelectedRole(role);
    setAuthError(null);
    setValue('email', '');
    setValue('password', '');
  };

  const autofillCredentials = (role: Role) => {
    const creds = demoCredentials[role];
    setValue('email', creds.email);
    setValue('password', creds.password);
    toast.success(`Demo credentials loaded for ${roles.find((r) => r.id === role)?.label}`);
  };

  const copyToClipboard = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setAuthError(null);
    try {
      const result = await signIn(data.email, data.password);
      const dbRole = result?.user?.user_metadata?.role ?? null;

      // Determine redirect: use profile role if available, else fall back to selected role route
      let redirectRoute = roles.find((r) => r.id === selectedRole)?.route ?? '/school-admin-dashboard';
      if (dbRole && DB_ROLE_ROUTE[dbRole]) {
        redirectRoute = DB_ROLE_ROUTE[dbRole];
      }

      toast.success(`Welcome back! Redirecting to your portal...`);
      setTimeout(() => router.push(redirectRoute), 600);
    } catch (error: any) {
      const msg = error?.message ?? 'Sign in failed. Please check your credentials.';
      setAuthError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[540px] flex-col gradient-primary relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(6)].map((_, i) => (
            <div
              key={`circle-${i}`}
              className="absolute rounded-full border border-white"
              style={{
                width: `${(i + 1) * 120}px`,
                height: `${(i + 1) * 120}px`,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          ))}
        </div>

        <div className="relative z-10 flex flex-col h-full p-10">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div>
              <span className="text-white font-bold text-xl tracking-tight">LearnerLink SA</span>
              <p className="text-blue-200 text-xs font-medium tracking-widest mt-0.5">CONNECT • MANAGE • EMPOWER</p>
            </div>
          </div>

          {/* Hero content */}
          <div className="flex-1 flex flex-col justify-center">
            <h1 className="text-hero text-white mb-6">
              South Africa&apos;s Trusted School Management Platform
            </h1>
            <p className="text-blue-100 text-base leading-relaxed mb-8">
              One secure platform for every school — manage learners, teachers, attendance,
              assessments, and parent communication with full data isolation between schools.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { value: '1,247', label: 'Active Schools' },
                { value: '384K+', label: 'Learners' },
                { value: '9 Provinces', label: 'Coverage' },
              ].map((stat) => (
                <div key={`stat-${stat.label}`} className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <p className="text-white font-bold text-2xl font-tabular">{stat.value}</p>
                  <p className="text-blue-200 text-xs mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-white/20 pt-6">
            <p className="text-blue-200 text-xs">
              © 2026 LearnerLink SA · Department of Basic Education Compliant
            </p>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-10 bg-background overflow-y-auto">
        <div className="w-full max-w-[480px]">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-3 mb-8">
            <AppImage
              src="/assets/images/LearnerLink_SA-1783193395309.png"
              alt="LearnerLink SA logo"
              width={40}
              height={40}
              className="rounded-lg"
            />
            <span className="font-bold text-lg text-primary">LearnerLink SA</span>
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-1">Sign in to your portal</h2>
          <p className="text-muted-foreground text-sm mb-6">Select your role and enter your credentials</p>

          {/* Role selector */}
          <div className="mb-6">
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-3">Your Role</p>
            <div className="grid grid-cols-1 gap-2">
              {roles.map((role) => (
                <button
                  key={`role-${role.id}`}
                  type="button"
                  onClick={() => handleRoleSelect(role.id)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-all duration-150 ${
                    selectedRole === role.id
                      ? 'border-primary bg-primary/5 text-primary' :'border-border bg-card text-foreground hover:border-primary/40 hover:bg-muted/50'
                  }`}
                >
                  <span className={selectedRole === role.id ? 'text-primary' : 'text-muted-foreground'}>
                    {role.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-600 text-sm">{role.label}</p>
                    <p className="text-xs text-muted-foreground truncate">{role.description}</p>
                  </div>
                  {selectedRole === role.id && <CheckCircle2 size={16} className="text-primary shrink-0" />}
                </button>
              ))}
            </div>
          </div>

          {/* Login form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Auth error */}
            {authError && (
              <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-danger/10 border border-danger/20 text-danger text-sm">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{authError}</span>
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-600 text-foreground mb-1.5">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                placeholder="your.email@school.co.za"
                className={`w-full px-4 py-2.5 rounded-lg border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 transition-all ${
                  errors.email ? 'border-danger' : 'border-border'
                }`}
                {...register('email', {
                  required: 'Email address is required',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
                })}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-danger flex items-center gap-1">
                  <AlertCircle size={12} />
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-600 text-foreground mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className={`w-full px-4 py-2.5 pr-10 rounded-lg border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 transition-all ${
                    errors.password ? 'border-danger' : 'border-border'
                  }`}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-danger flex items-center gap-1">
                  <AlertCircle size={12} />
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Remember me + forgot */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-border accent-primary"
                  {...register('rememberMe')}
                />
                <span className="text-sm text-muted-foreground">Remember me</span>
              </label>
              <button type="button" className="text-sm text-primary hover:underline font-500">
                Forgot password?
              </button>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg gradient-primary text-white font-600 text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In to {roles.find((r) => r.id === selectedRole)?.label} Portal
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-6 rounded-xl border border-border bg-muted/30 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/50">
              <p className="text-xs font-700 text-foreground uppercase tracking-wider">Demo Credentials</p>
              <button
                type="button"
                onClick={() => autofillCredentials(selectedRole)}
                className="text-xs text-primary font-600 hover:underline flex items-center gap-1"
              >
                <ArrowRight size={12} />
                Use {roles.find((r) => r.id === selectedRole)?.label}
              </button>
            </div>
            <div className="divide-y divide-border">
              {roles.map((role) => (
                <div
                  key={`cred-${role.id}`}
                  className={`px-4 py-2.5 transition-colors ${
                    selectedRole === role.id ? 'bg-primary/5' : 'hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-600 text-foreground mb-1">{role.label}</p>
                      <p className="text-xs text-muted-foreground font-mono truncate">{demoCredentials[role.id].email}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => autofillCredentials(role.id)}
                      className="shrink-0 px-2.5 py-1 text-xs font-600 text-primary border border-primary/30 rounded-md hover:bg-primary/5 transition-colors"
                    >
                      Use
                    </button>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(demoCredentials[role.id].email, `email-${role.id}`)}
                      className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Copy email"
                    >
                      {copiedField === `email-${role.id}` ? <CheckCircle2 size={14} className="text-success" /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Register CTA */}
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Is your school not registered yet?{' '}
              <a href="/school-registration-page" className="text-primary font-600 hover:underline">
                Register your school
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}